import { StateCreator } from "zustand";
import { State } from "./types";
import { initialState } from "./utils";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { usePatientStore } from "../Patient";
import { useCentralStore } from "../Central";
import { JobSearchResult } from "./types";

let currentSearchId = 0;

function getSearchSortName(item: {
	first_name?: string | null;
	last_name?: string | null;
	is_company?: boolean;
	company_name?: string | null;
}): string {
	if (item.is_company && item.company_name) {
		return item.company_name.toLowerCase();
	}
	const last = item.last_name?.toLowerCase() ?? "";
	const first = item.first_name?.toLowerCase() ?? "";
	if (last && first) return `${last} ${first}`;
	return last || first || "";
}

interface Actions {
	fetchSearchResults: (query: string) => Promise<void>;
	fetchSearchResultsClients: (
		query: string,
		searchId: number
	) => Promise<void>;
	fetchSearchResultsPatients: (
		query: string,
		searchId: number
	) => Promise<void>;
	fetchSearchResultsJobs: (query: string, searchId: number) => Promise<void>;
	resetSearchResults: () => Promise<void>;
}

export type SearchSlice = State & Actions;

export const createSearchStore: StateCreator<SearchSlice> = (set, get) => ({
	...initialState,

	fetchSearchResults: async (query) => {
		const searchId = ++currentSearchId;
		set({ loadingClients: true, loadingPatients: true, loadingJobs: true });
		await Promise.all([
			get().fetchSearchResultsClients(query, searchId),
			get().fetchSearchResultsPatients(query, searchId),
			get().fetchSearchResultsJobs(query, searchId),
		]);
	},

	fetchSearchResultsClients: async (query: string, searchId: number) => {
		const { data, error } = await supabase.rpc("search_clients", {
			query,
		});
		if (searchId !== currentSearchId) return;
		if (error) {
			Logger.log(error);
		} else {
			set({ searchResultsClients: data || [] });
		}
		set({ loadingClients: false });
	},

	fetchSearchResultsPatients: async (query: string, searchId: number) => {
		const patientResults = await usePatientStore
			.getState()
			.fetchPatientSearchResults(query);

		if (searchId !== currentSearchId) return;
		if (patientResults) {
			set({
				searchResultsPatients: patientResults.sort((a, b) =>
					getSearchSortName(a).localeCompare(getSearchSortName(b))
				),
			});
		}
		set({ loadingPatients: false });
	},

	fetchSearchResultsJobs: async (query: string, searchId: number) => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			Logger.error("No organization id found");
			set({ loadingJobs: false });
			return;
		}
		const { data, error } = await supabase.rpc("search_jobs", {
			query,
			p_organization_id: organizationId,
		});
		if (searchId !== currentSearchId) return;
		if (error) {
			Logger.log(error);
		} else {
			set({
				searchResultsJobs: data.sort(
					(a, b) =>
						new Date(b.title).getTime() -
						new Date(a.title).getTime()
				) as JobSearchResult[],
			});
		}
		set({ loadingJobs: false });
	},
	resetSearchResults: async () => {
		currentSearchId++;
		set({
			searchResultsClients: [],
			searchResultsPatients: [],
			searchResultsJobs: [],
			loadingClients: false,
			loadingPatients: false,
			loadingJobs: false,
		});
	},
});
